// EditableTextInline.tsx
import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Typography,
  InputBase,
  IconButton,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";

interface EditableTextInlineProps {
  value: string;
  onSave: (newValue: string) => void;
  multiline?: boolean;
  width?: number | string;
  type?: React.HTMLInputTypeAttribute;
}

const EditableTextInline: React.FC<EditableTextInlineProps> = ({
  value,
  onSave,
  multiline = true,
  width = "100%",
  type = "text",
}) => {
  // console.log('value', value);
  const [editing, setEditing] = useState(false);
  const [temp, setTemp] = useState(value);

  useEffect(() => {
    if (!editing) {
      setTemp(value);
    }
  }, [value, editing]);

  const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement | null>(null);

  // Автофокус при входе в режим редактирования
  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
    }
  }, [editing]);

  const handleSave = () => {
    onSave(temp.trim());
    setEditing(false);
  };

  const handleCancel = () => {
    setTemp(value);
    setEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !multiline) {
      e.preventDefault();
      handleSave();
    }
    if (e.key === "Escape") {
      handleCancel();
    }
  };

  // Ограничение ввода для type="number"
  const isNumber = type === "number";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    let val = e.target.value;
    if (isNumber) {
      // разрешаем только цифры, "-", "."
      if (!/^[-0-9.]*$/.test(val)) return;
      // не более одной точки
      if ((val.match(/\./g) || []).length > 1) return;
      // не более одного минуса и только в начале
      if ((val.match(/-/g) || []).length > 1) return;
      if (val.includes("-") && !val.startsWith("-")) return;
    }
    setTemp(val);
  };

  if (!editing) {
    return (
      <Box display="flex" alignItems="flex-start" gap={1} width={width}>
        <Typography sx={{ whiteSpace: "pre-line", flexGrow: 1 }}>
          {value}
        </Typography>

        <IconButton size="small" onClick={() => setEditing(true)}>
          <EditIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box display="flex" alignItems="flex-start" gap={1} width={width}>
      <InputBase
        inputRef={inputRef}
        type={type === "number" ? "text" : type}
        multiline={multiline}
        sx={{
          flexGrow: 1,
          padding: "4px 0",
          borderBottom: "1px solid rgba(0,0,0,0.3)",
        }}
        value={temp}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
      />

      <IconButton size="small" onClick={handleSave}>
        <CheckIcon fontSize="small" />
      </IconButton>

      <IconButton size="small" onClick={handleCancel}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export default EditableTextInline;
